/**
 * ProductCard Component
 * 
 * A reusable card component that displays product information including:
 * - Product image with fallback
 * - Title with variant size (if applicable)
 * - Store name
 * - Price information (including sale price if available)
 * 
 * Features:
 * - Handles product variants (sizes)
 * - Displays sale badges
 * - Links to product detail page with pre-selected variant
 * - Responsive hover effects
 * - Store hover tooltip with store exterior image
 *
 * IMPORTANT: This component expects to receive product data that includes:
 * - store or florist object containing location information (suburb, state)
 * - If the product is from ProductGrid.tsx, it will receive data in ProductCardMinimal format
 * - If the product is directly from the API, it may have a different structure
 */

import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Product } from '@/types/product';
import { MapPin, Store } from 'lucide-react';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';

/**
 * Props for the ProductCard component
 */
interface ProductCardProps {
  product: Product;
  className?: string;
  showStoreName?: boolean;
  isSearch?: boolean;
  isFloristCard?: boolean;
  onSendToCart?: (product: Product) => void;
  showFromPrice?: boolean; // Show "from" prefix when displaying grouped products
}

export const ProductCard = ({ 
  product, 
  showStoreName = true,
  className = "",
  isSearch = false,
  isFloristCard = false,
  onSendToCart,
  showFromPrice = false,
  ...props 
}: ProductCardProps) => {
  const navigate = useNavigate();
  
  // If product is null or missing required properties, return null
  if (!product || !product.id) return null;

  // Get the landscape store exterior image from cover_image_url
  const storefrontImage = product.store?.cover_image_url || product.florist?.cover_image_url || '/images/placeholder-storefront.png';

  /**
   * Gets the location (suburb, state) for a product from multiple possible sources:
   * 1. Direct properties on the store
   * 2. Direct properties on the florist
   * 3. Direct properties on the product itself
   * 4. Parsing the geocoded_address if it exists 
   * 5. Falling back to "Rosebery, NSW" if nothing else is available
   */
  const getLocation = () => {
    // More detailed debug output
    console.log('PRODUCT CARD - Full product data:', {
      id: product.id,
      title: product.title,
      store_id: product.store?.id,
      store_name: product.store?.store_name,
      store_suburb: product.store?.suburb,
      store_state: product.store?.state,
      // Check if store is a Proxy object
      store_is_proxy: product.store && typeof product.store === 'object' ? 
        Object.getPrototypeOf(product.store).constructor.name : 'not an object',
      store_keys: product.store ? Object.keys(product.store) : [],
      // If available
      florist_suburb: product.florist?.suburb,
      florist_state: product.florist?.state,
    });

    // Always prefer direct store data
    if (product.store?.suburb && product.store?.state) {
      console.log(`Using direct store location: ${product.store.suburb}, ${product.store.state}`);
      return `${product.store.suburb}, ${product.store.state}`;
    }

    // Fallback to florist data
    if (product.florist?.suburb && product.florist?.state) {
      console.log(`Using florist location: ${product.florist.suburb}, ${product.florist.state}`);
      return `${product.florist.suburb}, ${product.florist.state}`;
    }

    console.log('Falling back to default location: Rosebery, NSW');
    // Last resort fallback
    return "Rosebery, NSW";
  };
  
  /**
   * Get the store/florist name with fallbacks
   * Checks for both store_name and name properties to handle different data formats
   */
  const storeName = product.store?.store_name || 
                   (product.store as any)?.name || 
                   product.florist?.store_name || 
                   (product.florist as any)?.name;
  
  /**
   * Check if store is within delivery range
   * Used to determine if we should show "Free Delivery" or "Pickup Only"
   * 
   * @returns {boolean} Whether the store is within delivery range
   */
  const isWithinDeliveryRange = () => {
    // If there's no user coordinates or delivery radius, assume in range
    if (!product.store?.coordinates) return true;
    
    // If the store has a calculated_distance property, use it to check
    if (product.store.calculated_distance !== undefined && product.store.delivery_radius !== undefined) {
      return product.store.calculated_distance <= product.store.delivery_radius;
    }
    
    // Default to in range if we can't determine
    return true;
  };
  
  return (
    <Link
      to={`/product/${product.id}${product.selectedVariant ? `/${product.selectedVariant.id}` : ''}`}
      className={cn(
        "group relative bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 border border-black/10",
        className
      )}
    >
      {/* Image Container - Square */}
      <div className="relative aspect-square overflow-hidden">
        <img
          src={product.images[0]}
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
      </div>

      {/* Content */}
      <div className="p-3 pt-2">
        {/* Title, Size, and Price - Three Line Layout */}
        <div className="flex flex-col space-y-0.5">
          <span className="text-[13px] text-[#2C1810]">{product.title}</span>
          {(product.selectedVariant?.size_name || product.selectedVariant?.size || product.variants?.[0]?.size_name) && (
            <span className="text-[13px] text-[#2C1810]">
              {product.selectedVariant?.size_name || product.selectedVariant?.size || product.variants?.[0]?.size_name}
            </span>
          )}
          <span className="text-[13px] text-[#2C1810]">
            {showFromPrice && 'from '}${product.price}
          </span>
        </div>
        
        {/* Florist and Location Buttons */}
        <div className="flex items-center justify-between gap-2 mt-0.5 flex-wrap">
          {showStoreName && (
            <Link
              to={`/florist/${product.store?.id || product.florist?.id}`}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1 bg-white border border-[#967F81]/20 text-[13px] text-[#2C1810] rounded-full font-normal hover:bg-[#843329]/20 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Store className="w-3.5 h-3.5" />
              {storeName}
            </Link>
          )}
          
          <Link
            to={`/product/${product.id}${product.selectedVariant ? `/${product.selectedVariant.id}` : ''}`}
            className={`inline-flex items-center justify-center gap-1.5 px-3 py-1 bg-[#967F81]/30 border border-[#967F81] text-[13px] text-[#2C1810] rounded-full font-normal ${!showStoreName ? 'w-full justify-center' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="whitespace-nowrap">{getLocation()}</span>
          </Link>
        </div>
      </div>
    </Link>
  );
} 